import { resetIsContextMenuOpenForLinks, setIsContextMenuOpen } from '../actions/internalActions';
import getComposeLinkViewState from '../selectors/getComposeLinkViewState';
import ComposeLinkPreviewMode from '../store/schema/ComposeLinkPreviewMode';
import ComposeLinkViewState from '../store/schema/ComposeLinkViewState';
import { LINK_CONTEXT_MENU } from '../utils/constants';
import { getAttachACopyContextMenuItem } from '../utils/contextMenu/getAttachACopyContextMenuItem';
import { getChangePermissionContextMenuItem } from '../utils/contextMenu/getChangePermissionContextMenuItem';
import { getChangePermissionSharingDialogContextMenuItem } from '../utils/contextMenu/getChangePermissionSharingDialogContextMenuItem';
import { getCopyLinkContextMenuItem } from '../utils/contextMenu/getCopyLinkContextMenuItem';
import { getDividerForContextMenu } from '../utils/contextMenu/getDividerForContextMenu';
import { getEditLinkContextMenuItem } from '../utils/contextMenu/getEditLinkContextMenuItem';
import { getLinkPreviewContextMenuItem } from '../utils/contextMenu/getLinkPreviewContextMenuItem';
import { getRevertToFullUrlContextMenuItem } from '../utils/contextMenu/getRevertToFullUrlContextMenuItem';
import { getSharingTipContextMenuItems } from '../utils/contextMenu/getSharingTipContextMenuItems';
import getLinkPreviewMode from '../utils/getLinkPreviewMode';
import { observer } from 'mobx-react';
import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { getOpenInNewTabContextMenuItem } from 'owa-attachment-link-common';
import { lazyDocLinkHadPreviewInfo } from 'owa-doc-link-click-handler';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    getSharingDataFromLink,
    getSharingLink,
    lazyGetSharingTipRecipientInfo,
    LinkActionStatus,
    RecipientContainer,
    recipientContainerIsRecipientWell,
} from 'owa-link-data';
import { lazyGetSharingIssuesForSharingData, SharingTipRecipientInfo } from 'owa-sharing-data';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Editor } from 'roosterjs-editor-core';
import { PositionType } from 'roosterjs-editor-types';
import getLinkContextMenuOptions, {
    LinkContextMenuOptions,
} from '../utils/contextMenu/LinkContextMenuOptions';
import {
    ContextualMenu,
    DirectionalHint,
    IContextualMenuItem,
} from 'office-ui-fabric-react/lib/ContextualMenu';

const styles = require('./LinkContextMenu.scss');
const classNames = require('classnames/bind').bind(styles);

interface LinkContextMenuProps {
    composeLinkViewState: ComposeLinkViewState;
    anchorElement: HTMLAnchorElement;
    editor: Editor;
    composeId: string;
    recipientInfo: SharingTipRecipientInfo[];
    isCalendar: boolean;
}

@observer
class LinkContextMenu extends React.Component<LinkContextMenuProps, {}> {
    render() {
        // When the context menu is shown & user uses Ctrl+Z to undo beautification,
        // the sharingLink associated with the linkId would be removed as the old linkId is considered as being removed.
        const sharingLinkInfo = getSharingLink(this.props.composeLinkViewState.linkId);

        // We do not show the context menu while attaching as a copy is in progress
        if (
            !sharingLinkInfo ||
            sharingLinkInfo.linkActionStatus === LinkActionStatus.attachingAsACopy ||
            sharingLinkInfo.linkActionStatus === LinkActionStatus.pendingAttachAsACopy
        ) {
            return null;
        }

        const [items, hasIcon] = getContextMenuItemsAndHasIcon(
            this.props.composeLinkViewState,
            this.props.anchorElement,
            this.props.editor,
            this.props.composeId,
            this.props.recipientInfo,
            this.props.isCalendar
        );

        // If the sharing dialog flight is on, the permission menu item will always have an icon
        const conditionalStyles = {
            conditionalPadding: hasIcon,
        };
        const shouldFocusOnMount = isFeatureEnabled('doc-attachment-contextualSharingTips');

        return (
            <ContextualMenu
                className={classNames(conditionalStyles)}
                target={this.props.anchorElement}
                shouldFocusOnMount={shouldFocusOnMount}
                onDismiss={this.onDismissContextMenu}
                items={items}
                directionalHint={DirectionalHint.bottomLeftEdge}
                gapSpace={2}
            />
        );
    }

    private onDismissContextMenu = (ev: any) => {
        closeLinkContextMenu();
    };
}

let contextMenuContainer: HTMLSpanElement = null;
export async function showLinkContextMenu(
    anchorElement: HTMLAnchorElement,
    editor: Editor,
    composeId: string,
    recipientContainers: RecipientContainer[],
    fromAddress: string
) {
    closeLinkContextMenu();

    const getSharingTipRecipientInfo = lazyGetSharingTipRecipientInfo.tryImportForRender();
    let recipientInfo: SharingTipRecipientInfo[] = [];
    if (getSharingTipRecipientInfo) {
        recipientInfo = getSharingTipRecipientInfo(recipientContainers, fromAddress);
    }
    const isCalendar = !recipientContainers.some(well => recipientContainerIsRecipientWell(well));

    contextMenuContainer = document.createElement('span');
    contextMenuContainer.id = 'sharingLinkContextMenu';
    anchorElement.appendChild(contextMenuContainer);

    ReactDOM.render(
        <LinkContextMenu
            composeLinkViewState={getComposeLinkViewState(anchorElement.id)}
            anchorElement={anchorElement}
            editor={editor}
            composeId={composeId}
            recipientInfo={recipientInfo}
            isCalendar={isCalendar}
        />,
        contextMenuContainer
    );

    setIsContextMenuOpen(anchorElement.id, true);

    // Note - we compute hasSharingIssues again for logging even though it is computed in the context menu because
    // we want getContextMenuItemsAndHasIcon to be in render, so that the menu will update when preview becomes available
    // or a sharingIssue is detected. We could locally store hasSharingIssue, but there is a risk of it being
    // out of sync.
    const getSharingIssuesForSharingData = await lazyGetSharingIssuesForSharingData.import();
    const sharingData = getSharingDataFromLink(anchorElement.id);
    const hasSharingIssues: boolean =
        getSharingIssuesForSharingData(sharingData, recipientInfo).length > 0;
    logUsage('LinkContextMenuOpened', { hasSharingIssue: hasSharingIssues });
}

function getContextMenuItemsAndHasIcon(
    composeLinkViewState: ComposeLinkViewState,
    anchorElement: HTMLAnchorElement,
    editor: Editor,
    composeId: string,
    recipientInfo: SharingTipRecipientInfo[],
    isCalendar: boolean
): [IContextualMenuItem[], boolean] {
    let hasSharingTip: boolean = false;
    let hasChangePermissionIcon: boolean = false;

    const contextMenuItems: IContextualMenuItem[] = [];
    const contextMenuOptions = getLinkContextMenuOptions();
    const replaceLinkHandler: ReplaceLinkHandler = (url: string, displayText: string) => {
        if (!editor.isDisposed()) {
            editor.addUndoSnapshot(() => updateLink(editor, anchorElement, url, displayText));
        }
    };

    contextMenuOptions.forEach(contextMenu => {
        switch (contextMenu) {
            case LinkContextMenuOptions.SharingTip:
                const sharingTipContextMenuItems = getSharingTipContextMenuItems(
                    composeLinkViewState,
                    recipientInfo,
                    composeId
                );
                if (sharingTipContextMenuItems && sharingTipContextMenuItems.length > 0) {
                    hasSharingTip = true;
                    sharingTipContextMenuItems.forEach((item, index) => {
                        contextMenuItems.push(item);
                        contextMenuItems.push(getDividerForContextMenu(`divider_sharing ${index}`));
                    });
                }
                break;
            case LinkContextMenuOptions.OpenInNewTab:
                const OpenInNewTabMenuItem = getOpenInNewTabContextMenuItem(
                    getSharingLink(composeLinkViewState.linkId).url,
                    () => {
                        closeLinkContextMenu();
                        logUsage('OpenInNewTab', {
                            entryPoint: 'BeautifulLinkContextMenu',
                        });
                    }
                );
                if (OpenInNewTabMenuItem) {
                    contextMenuItems.push(OpenInNewTabMenuItem);
                }
                break;
            case LinkContextMenuOptions.AttachACopy:
                const AttachACopyMenuItem = getAttachACopyContextMenuItem(
                    composeLinkViewState.linkId
                );
                if (AttachACopyMenuItem) {
                    contextMenuItems.push(AttachACopyMenuItem);
                }
                break;
            case LinkContextMenuOptions.ChangePermission:
                const ChangePermissionSharingDialogMenuItem = getChangePermissionSharingDialogContextMenuItem(
                    composeLinkViewState.linkId,
                    composeId,
                    recipientInfo
                );
                if (ChangePermissionSharingDialogMenuItem) {
                    contextMenuItems.push(ChangePermissionSharingDialogMenuItem);
                    contextMenuItems.push(getDividerForContextMenu('divider_perms'));
                    hasChangePermissionIcon = true;
                } else {
                    // We only show the normal change permission menu item if the sharing dialog isn't available
                    const ChangePermissionMenuItem = getChangePermissionContextMenuItem(
                        composeLinkViewState,
                        composeId,
                        recipientInfo
                    );
                    if (ChangePermissionMenuItem) {
                        contextMenuItems.push(ChangePermissionMenuItem);
                        contextMenuItems.push(getDividerForContextMenu('divider_perms'));
                    }
                }
                break;
            case LinkContextMenuOptions.EditLink:
                if (composeLinkViewState.isLinkBeautified) {
                    const EditLinkContextMenuItem = getEditLinkContextMenuItem(
                        anchorElement,
                        replaceLinkHandler
                    );
                    if (EditLinkContextMenuItem) {
                        contextMenuItems.push(EditLinkContextMenuItem);
                    }
                }
                break;
            case LinkContextMenuOptions.RevertToFullUrl:
                if (composeLinkViewState.isLinkBeautified) {
                    contextMenuItems.push(
                        getRevertToFullUrlContextMenuItem(
                            composeLinkViewState.linkId,
                            anchorElement,
                            replaceLinkHandler,
                            composeId
                        )
                    );
                }
                break;
            case LinkContextMenuOptions.Preview:
                if (isFeatureEnabled('doc-SxS-jsApi-ODB-Links')) {
                    const docLinkHadPreviewInfo = lazyDocLinkHadPreviewInfo.tryImportForRender();
                    const url = getSharingLink(composeLinkViewState.linkId).url;
                    if (
                        docLinkHadPreviewInfo &&
                        docLinkHadPreviewInfo(url) &&
                        getLinkPreviewMode(composeLinkViewState.linkId) ===
                            ComposeLinkPreviewMode.None
                    ) {
                        const linkPreviewContextMenuItem = getLinkPreviewContextMenuItem(
                            url,
                            composeLinkViewState.linkId,
                            isCalendar
                        );
                        if (linkPreviewContextMenuItem) {
                            contextMenuItems.push(linkPreviewContextMenuItem);
                        }
                    }
                }
                break;
            case LinkContextMenuOptions.CopyLinkToClipboard:
                const CopyLinkContextMenuItem = getCopyLinkContextMenuItem(anchorElement);
                if (CopyLinkContextMenuItem) {
                    contextMenuItems.push(CopyLinkContextMenuItem);
                }
                break;
            default:
                assertNever(contextMenu);
        }
    });

    return [contextMenuItems, hasSharingTip || hasChangePermissionIcon];
}

export function closeLinkContextMenu() {
    if (contextMenuContainer) {
        ReactDOM.unmountComponentAtNode(contextMenuContainer);
        if (contextMenuContainer.parentNode) {
            contextMenuContainer.parentNode.removeChild(contextMenuContainer);
        }
        contextMenuContainer = null;
    }

    resetIsContextMenuOpenForLinks();
}

function updateLink(
    editor: Editor,
    anchorElement: HTMLAnchorElement,
    url: string,
    displayText: string
) {
    // Remove the anchor element & insert a new anchor element
    ReactDOM.unmountComponentAtNode(anchorElement);
    const newAnchor: HTMLAnchorElement = document.createElement('a');
    newAnchor.href = url;
    newAnchor.innerText = displayText;
    editor.replaceNode(anchorElement, newAnchor);
    editor.select(newAnchor, PositionType.After);
    editor.triggerContentChangedEvent(LINK_CONTEXT_MENU);
}

export type ReplaceLinkHandler = (url: string, displayText: string) => void;
